import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';

// =============================================================================
// ADMIN AUTH CONFIG
// Simple auth - in production, use proper authentication
// =============================================================================

const ADMIN_CREDENTIALS = {
  username: 'admin',
  password: 'Autonomous2024!'  // Change this!
};

const AUTH_KEY = 'autonomous_admin_auth';
const AUTH_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// =============================================================================
// AUTH HELPERS
// =============================================================================

export function isAuthenticated() {
  if (typeof window === 'undefined') return false;
  
  const auth = localStorage.getItem(AUTH_KEY);
  if (!auth) return false;
  
  try {
    const { expiry } = JSON.parse(auth);
    if (Date.now() > expiry) {
      localStorage.removeItem(AUTH_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function setAuthenticated() {
  localStorage.setItem(AUTH_KEY, JSON.stringify({
    authenticated: true,
    expiry: Date.now() + AUTH_EXPIRY
  }));
}

export function logout() {
  localStorage.removeItem(AUTH_KEY);
}

// =============================================================================
// COLORS
// =============================================================================

const c = {
  bg: '#000000',
  bgCard: 'rgba(255, 255, 255, 0.03)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.7)',
  textSubtle: 'rgba(255, 255, 255, 0.5)',
  border: 'rgba(255, 255, 255, 0.08)',
  error: '#ef4444',
  accent: '#007AFF',
};

// =============================================================================
// LOGIN PAGE COMPONENT
// =============================================================================

export default function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      const from = location.state?.from?.pathname || '/admin/leads';
      navigate(from, { replace: true });
    }
  }, [navigate, location]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (username === ADMIN_CREDENTIALS.username && password === ADMIN_CREDENTIALS.password) {
      setAuthenticated();
      const from = location.state?.from?.pathname || '/admin/leads';
      navigate(from, { replace: true });
    } else {
      setError('Feil brukernavn eller passord');
      setLoading(false);
    }
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      background: c.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&display=swap');
        * { box-sizing: border-box; }
        input::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          width: '100%',
          maxWidth: '400px',
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <span style={{ fontSize: '48px', fontWeight: '100', color: c.text }}>a.</span>
          <div style={{ fontSize: '14px', color: c.textSubtle, marginTop: '8px' }}>
            Admin Dashboard
          </div>
        </div>
        
        {/* Login Card */}
        <div style={{
          background: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: '16px',
          padding: '32px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            background: 'rgba(255,255,255,0.05)',
            margin: '0 auto 24px',
          }}>
            <Lock size={24} color={c.textMuted} />
          </div>
          
          <h1 style={{
            fontSize: '20px',
            fontWeight: '500',
            color: c.text,
            textAlign: 'center',
            marginBottom: '8px',
          }}>
            Logg inn
          </h1>
          
          <p style={{
            fontSize: '14px',
            color: c.textSubtle,
            textAlign: 'center',
            marginBottom: '32px',
          }}>
            Tilgang til CRM og administrasjon
          </p>
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: c.textSubtle,
                marginBottom: '8px',
              }}>
                Brukernavn
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                required
                autoComplete="username"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '15px',
                  background: 'rgba(255,255,255,0.05)',
                  border: `1px solid ${error ? c.error : c.border}`,
                  borderRadius: '10px',
                  color: c.text,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                onBlur={(e) => e.target.style.borderColor = error ? c.error : c.border}
              />
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: c.textSubtle,
                marginBottom: '8px',
              }}>
                Passord
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  style={{
                    width: '100%',
                    padding: '14px 48px 14px 16px',
                    fontSize: '15px',
                    background: 'rgba(255,255,255,0.05)',
                    border: `1px solid ${error ? c.error : c.border}`,
                    borderRadius: '10px',
                    color: c.text,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                  onBlur={(e) => e.target.style.borderColor = error ? c.error : c.border}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: c.textSubtle,
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 14px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '8px',
                  marginBottom: '20px',
                }}
              >
                <AlertCircle size={16} color={c.error} />
                <span style={{ fontSize: '13px', color: c.error }}>{error}</span>
              </motion.div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 24px',
                fontSize: '15px',
                fontWeight: '500',
                background: c.text,
                color: c.bg,
                border: 'none',
                borderRadius: '10px',
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'transform 0.2s, opacity 0.2s',
              }}
              onMouseOver={(e) => !loading && (e.target.style.transform = 'scale(1.02)')}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            >
              {loading ? 'Logger inn...' : 'Logg inn'}
            </button>
          </form>
        </div>
        
        {/* Footer */}
        <p style={{
          textAlign: 'center',
          fontSize: '12px',
          color: c.textSubtle,
          marginTop: '24px',
        }}>
          © {new Date().getFullYear()} Autonomous AS
        </p>
      </motion.div>
    </div>
  );
}
